/// <reference types="node" />

interface LoginResponse {
  success: boolean;
  data: {
    access_token?: string;
    refresh_token?: string;
    id_token?: string;
    error?: string;
    error_description?: string;
  };
}

interface User {
  email?: string;
  name?: string;
  picture?: string;
  nickname?: string;
  given_name?: string;
  family_name?: string;
  sub?: string;  // Auth0 user ID
  user_metadata?: {
    [key: string]: any;
  };
}

interface UpdateProfileData {
  name?: string;
  given_name?: string;
  family_name?: string;
  nickname?: string;
  user_metadata?: {
    [key: string]: any;
  };
}

let currentUser: User | null = null;
let accessToken: string | null = null;

export const login = async (username: string, password: string): Promise<LoginResponse> => {
  try {
    const domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

    const response = await fetch(`https://${domain}/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'password',
        username,
        password,
        client_id: clientId,
        client_secret: process.env.REACT_APP_AUTH0_CLIENT_SECRET,
        audience,
        scope: 'openid profile email'
      }),
    });

    const data = await response.json();
    
    if (response.ok) {
      accessToken = data.access_token;
      
      // Store tokens and parse user info
      accessToken = data.access_token;
      sessionStorage.setItem('access_token', data.access_token);
      
      // Store refresh token if provided
      if (data.refresh_token) {
        sessionStorage.setItem('refresh_token', data.refresh_token);
      }

      if (!data.id_token) {
        console.warn('No id_token received');
        return { success: true, data };
      }

      try {
        // Get user info from Auth0
        const domain = process.env.REACT_APP_AUTH0_DOMAIN;
        const userInfoResponse = await fetch(`https://${domain}/userinfo`, {
          headers: {
            'Authorization': `Bearer ${data.access_token}`
          }
        });

        if (!userInfoResponse.ok) {
          throw new Error('Failed to fetch user info');
        }

        const userInfo = await userInfoResponse.json();
        currentUser = {
          email: userInfo.email,
          name: userInfo.name || userInfo.email,
          picture: userInfo.picture,
          nickname: userInfo.nickname,
          given_name: userInfo.given_name,
          family_name: userInfo.family_name,
          sub: userInfo.sub,
          user_metadata: userInfo.user_metadata || {}
        };
        
        // Store tokens and user data
        sessionStorage.setItem('id_token', data.id_token);
        sessionStorage.setItem('user', JSON.stringify(currentUser));
        
        // Verify everything is stored before returning success
        const storedToken = sessionStorage.getItem('id_token');
        const storedUser = sessionStorage.getItem('user');
        
        if (!storedToken || !storedUser) {
          throw new Error('Failed to persist auth state');
        }

        // Force a check of authentication state
        const isValid = await isAuthenticated();
        if (!isValid) {
          throw new Error('Failed to validate authentication state');
        }
        
        return { success: true, data };
      } catch (error) {
        console.error('Failed to process login:', error);
        return { 
          success: false, 
          data: { 
            error: 'auth_error', 
            error_description: 'Failed to process authentication' 
          } 
        };
      }
    }
    
    return { success: false, data };
  } catch (error) {
    return {
      success: false,
      data: { error: 'network_error', error_description: 'Failed to connect to Auth0' }
    };
  }
};

export const logout = async () => {
  console.log('Clearing auth state...');
  // Clear memory state
  currentUser = null;
  accessToken = null;

  // Clear storage
  try {
    // Remove specific tokens
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('user');
    // Clear any other stored data
    sessionStorage.clear();
    console.log('Auth state cleared');
    
    // Redirect to Auth0 logout URL to clear Auth0 session
    window.location.href = `https://${process.env.REACT_APP_AUTH0_DOMAIN}/v2/logout?client_id=${process.env.REACT_APP_AUTH0_CLIENT_ID}&returnTo=${window.location.origin}`;
  } catch (error) {
    console.error('Error clearing auth state:', error);
    throw error;
  }
};

export const getUser = async (): Promise<User | null> => {
  console.log('Getting user data...');
  
  // First try to get from memory
  if (currentUser) {
    console.log('Returning user from memory');
    return currentUser;
  }
  
  // Then try to get from session storage
  const storedUser = sessionStorage.getItem('user');
  if (storedUser) {
    try {
      currentUser = JSON.parse(storedUser);
      console.log('Returning user from session storage');
      return currentUser;
    } catch (error) {
      console.error('Failed to parse stored user:', error);
    }
  }
  
  // Finally try to get fresh data from Auth0
  const token = await getToken();
  if (token) {
    try {
      const domain = process.env.REACT_APP_AUTH0_DOMAIN;
      const userInfoResponse = await fetch(`https://${domain}/userinfo`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!userInfoResponse.ok) {
        throw new Error('Failed to fetch user info');
      }

      const userInfo = await userInfoResponse.json();
      currentUser = {
        email: userInfo.email,
        name: userInfo.name || userInfo.email,
        picture: userInfo.picture,
        nickname: userInfo.nickname,
        given_name: userInfo.given_name,
        family_name: userInfo.family_name,
        sub: userInfo.sub,
        user_metadata: userInfo.user_metadata || {}
      };
      
      // Store user data
      sessionStorage.setItem('user', JSON.stringify(currentUser));
      console.log('Returning fresh user data from Auth0');
      return currentUser;
    } catch (error) {
      console.error('Failed to restore user from id_token:', error);
    }
  }
  
  console.log('No user data found');
  return null;
};

export const updateProfile = async (data: UpdateProfileData): Promise<boolean> => {
  try {
    const token = await getToken();
    if (!token) {
      throw new Error('No access token available');
    }

    const domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const userId = currentUser?.sub; // Auth0 user ID from decoded token

    // Get a management API token
    const tokenResponse = await fetch(`https://${domain}/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        grant_type: 'client_credentials',
        client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
        client_secret: process.env.REACT_APP_AUTH0_CLIENT_SECRET,
        audience: `https://${domain}/api/v2/`
      })
    });

    if (!tokenResponse.ok) {
      throw new Error('Failed to get management API token');
    }

    const { access_token: managementToken } = await tokenResponse.json();

    // Update user profile
    const response = await fetch(`https://${domain}/api/v2/users/${userId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${managementToken}`
      },
      body: JSON.stringify({
        name: data.name,
        given_name: data.given_name,
        family_name: data.family_name,
        nickname: data.nickname,
        user_metadata: data.user_metadata
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update profile');
    }

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Profile update error:', errorData);
      throw new Error('Failed to update profile');
    }

    // Get fresh user data
    const userInfoResponse = await fetch(`https://${domain}/userinfo`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!userInfoResponse.ok) {
      throw new Error('Failed to fetch updated user info');
    }

    const userInfo = await userInfoResponse.json();
    currentUser = {
      email: userInfo.email,
      name: userInfo.name || userInfo.email,
      picture: userInfo.picture,
      nickname: userInfo.nickname,
      given_name: userInfo.given_name,
      family_name: userInfo.family_name,
      sub: userInfo.sub,
      user_metadata: userInfo.user_metadata || {}
    };
    sessionStorage.setItem('user', JSON.stringify(currentUser));

    return true;
  } catch (error) {
    console.error('Failed to update profile:', error);
    return false;
  }
};

export const getToken = async (): Promise<string | null> => {
  if (!accessToken) {
    accessToken = sessionStorage.getItem('access_token');
  }
  
  // Check if we have a token and if it's close to expiring
  if (accessToken) {
    try {
      // Decode token to check expiration
      const base64Url = accessToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => 
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join(''));
      
      const payload = JSON.parse(jsonPayload);
      const expTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiration = expTime - currentTime;
      
      // If token expires in less than 5 minutes (300000ms), try to refresh it
      if (timeUntilExpiration < 300000 && timeUntilExpiration > 0) {
        console.log('Token expiring soon, attempting to refresh');
        const refreshToken = sessionStorage.getItem('refresh_token');
        
        if (refreshToken) {
          try {
            const refreshResult = await refreshAccessToken(refreshToken);
            if (refreshResult) {
              console.log('Token refreshed successfully');
              return accessToken; // Now updated in memory and storage
            }
          } catch (error) {
            console.error('Failed to refresh token:', error);
            // Continue with existing token
          }
        }
      } else if (timeUntilExpiration <= 0) {
        // Token already expired
        console.error('Token already expired');
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('id_token');
        sessionStorage.removeItem('user');
        accessToken = null;
        return null;
      }
    } catch (error) {
      console.error('Error checking token expiration:', error);
      // If we can't decode the token, just return it as is
    }
  }
  
  return accessToken;
};

// Helper function to refresh the access token
const refreshAccessToken = async (refreshToken: string): Promise<boolean> => {
  try {
    const domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
    const clientSecret = process.env.REACT_APP_AUTH0_CLIENT_SECRET;
    
    const response = await fetch(`https://${domain}/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        grant_type: 'refresh_token',
        client_id: clientId,
        client_secret: clientSecret,
        refresh_token: refreshToken
      })
    });
    
    if (response.ok) {
      const data = await response.json();
      accessToken = data.access_token;
      sessionStorage.setItem('access_token', data.access_token);
      
      // Update id_token if provided
      if (data.id_token) {
        sessionStorage.setItem('id_token', data.id_token);
      }
      
      return true;
    } else {
      console.error('Token refresh failed:', await response.text());
      return false;
    }
  } catch (error) {
    console.error('Error refreshing token:', error);
    return false;
  }
};

export const isAuthenticated = async (): Promise<boolean> => {
  try {
    console.log('Checking authentication state...');
    
    // Check for token
    const token = await getToken();
    if (!token) {
      console.log('No token found during auth check');
      return false;
    }

    // Verify token expiry
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => 
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''));
    
    const payload = JSON.parse(jsonPayload);
    const isTokenValid = Date.now() < payload.exp * 1000;
    
    if (!isTokenValid) {
      console.log('Token expired during auth check');
      return false;
    }

    // Verify user data exists
    const user = await getUser();
    if (!user) {
      console.log('No user data found during auth check');
      return false;
    }

    console.log('Auth check passed - token valid and user exists');
    return true;
  } catch (error) {
    console.error('Failed to verify authentication:', error);
    return false;
  }
};
